import {useMemo, useState} from "react";
import PropTypes from "prop-types";
import {v4 as uuidv4} from "uuid";
import {IconButton, Table as MuiTable, TablePagination,} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import SoftBox from "../../../components/SoftBox";
import SoftAvatar from "../../../components/Avatar";
import SoftTypography from "../../../components/SoftTypography";
import SoftButton from "../../../components/SoftButton";
import colors from "../../../assets/theme/base/colors";
import typography from "../../../assets/theme/base/typography";
import borders from "../../../assets/theme/base/borders";
import DeleteIcon from "@mui/icons-material/Delete";
import {Edit, Visibility} from "@mui/icons-material";

function GuardianTable({columns = [], rows = [{}], handleDeleteClick, handleEditClick, handleViewDetails}) {
    const {light} = colors;
    const {size, fontWeightBold} = typography;
    const {borderWidth} = borders;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return rows.slice(start, start + rowsPerPage);
    }, [rows, page, rowsPerPage]);

    const renderColumns = columns.map(({name, align, width}, key) => {
        const pl = key === 0 ? 3 : 1;
        const pr = key === columns.length - 1 ? 3 : 1;

        return (
            <SoftBox
                key={name}
                component="th"
                width={width || "auto"}
                pt={1.5}
                pb={1.25}
                pl={align === "left" ? pl : 3}
                pr={align === "right" ? pr : 3}
                textAlign={align}
                fontSize={size.xxs}
                fontWeight={fontWeightBold}
                color="secondary"
                opacity={0.7}
                borderBottom={`${borderWidth[1]} solid ${light.main}`}
            >
                {name.toUpperCase()}
            </SoftBox>
        );
    });

    const renderRows = paginatedRows.map((row, key) => {
        const rowKey = `row-${key}`;

        const tableRow = columns.map(({value, align}) => {
            let template;

            if (Array.isArray(row[value])) {
                template = (
                    <SoftBox
                        key={uuidv4()}
                        component="td"
                        p={1}
                        borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
                    >
                        <SoftBox display="flex" alignItems="center" py={0.5} px={1}>
                            <SoftBox mr={2}>
                                <SoftAvatar src={row[value][0]} name={row[value][1]} variant="rounded" size="sm"/>
                            </SoftBox>
                            <SoftTypography variant="button" fontWeight="medium" sx={{width: "max-content"}}>
                                {row[value][1]}
                            </SoftTypography>
                        </SoftBox>
                    </SoftBox>
                );
            } else if (value === "profilePic" && row[value]) {
                template = (
                    <SoftBox
                        key={uuidv4()}
                        component="td"
                        p={1}
                        textAlign={align}
                        borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
                    >
                        <SoftAvatar
                            src={`data:image/png;base64,${row[value]}`}
                            alt="profile-image"
                            variant="rounded"
                            size="sm"
                            shadow="sm"
                        />
                    </SoftBox>
                );
            } else if (value === "userRole") {
                template = (
                    <SoftBox
                        key={uuidv4()}
                        component="td"
                        p={1}
                        textAlign={align}
                        borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
                    >
                        <SoftTypography
                            variant="button"
                            fontWeight="regular"
                            color="secondary"
                            sx={{display: "inline-block", width: "max-content"}}
                        >
                            {row[value]?.replace('_', ' ')}
                        </SoftTypography>
                    </SoftBox>
                );
            } else if (value === "status") {
                const getStatusColor = (status) => {
                    switch (status) {
                        case "ACTIVE":
                            return "success";
                        case "INACTIVE":
                            return "error";
                        case "IDLE":
                            return "warning";
                        default:
                            return "info";
                    }
                };
                template = (
                    <SoftBox
                        key={uuidv4()}
                        component="td"
                        p={1}
                        textAlign={align}
                        borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
                    >
                        <SoftButton
                            variant="gradient"
                            color={getStatusColor(row[value])}
                            size="small"
                            sx={{minWidth: 80}}
                        >
                            {row[value]}
                        </SoftButton>
                    </SoftBox>
                );
            } else if (value === "action") {
                template = (
                    <SoftBox
                        key={uuidv4()}
                        component="td"
                        p={1}
                        textAlign={align}
                        borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
                    >
                        <SoftBox display="flex" gap={1} justifyContent="center">
                            <IconButton
                                color="info"
                                onClick={() => handleViewDetails(row)}
                                size="small"
                                title="View Details"
                            >
                                <Visibility/>
                            </IconButton>
                            <IconButton
                                color="primary"
                                onClick={() => handleEditClick(row)}
                                size="small"
                                title="Edit"
                            >
                                <Edit/>
                            </IconButton>
                            <IconButton
                                color="error"
                                onClick={() => handleDeleteClick(row)}
                                size="small"
                                title="Delete"
                            >
                                <DeleteIcon/>
                            </IconButton>
                        </SoftBox>
                    </SoftBox>
                );
            } else {
                template = (
                    <SoftBox
                        key={uuidv4()}
                        component="td"
                        p={1}
                        textAlign={align}
                        borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
                    >
                        <SoftTypography
                            variant="button"
                            fontWeight="regular"
                            color="secondary"
                            sx={{display: "inline-block", width: "max-content"}}
                        >
                            {row[value]}
                        </SoftTypography>
                    </SoftBox>
                );
            }

            return template;
        });

        return <TableRow key={rowKey}>{tableRow}</TableRow>;
    });

    return useMemo(() => (
        <>
            <TableContainer>
                <MuiTable>
                    <SoftBox component="thead">
                        <TableRow>{renderColumns}</TableRow>
                    </SoftBox>
                    <TableBody>{renderRows}</TableBody>
                </MuiTable>
            </TableContainer>
            <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Rows:"
                sx={{
                    ".MuiTablePagination-toolbar": {
                        width: "75%",
                        justifyContent: "space-between",
                    },
                    ".MuiTablePagination-selectLabel": {
                        marginRight: 1,
                        flexShrink: 0,
                        width: "10% !important",
                    },
                    "MuiInputBase-root-MuiTablePagination-select": {
                        width: "10% !important",
                    },
                    ".MuiTablePagination-select": {
                        maxWidth: "10px",
                        width: "10% !important",
                    },
                    ".MuiSelect-select": {
                        maxWidth: "10%",
                        paddingRight: 0,
                        width: "10% !important",
                    },
                    ".MuiSelect-nativeInput": {
                        maxWidth: "10%",
                        paddingRight: 0,
                        width: "10% !important",
                    },
                    ".MuiTablePagination-actions": {
                        width: "10%",
                        paddingRight: 0,
                    },
                }}
            />
        </>
    ), [renderRows, renderColumns, rows.length, page, rowsPerPage]);
}

GuardianTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object),
    rows: PropTypes.arrayOf(PropTypes.object),
    handleDeleteClick: PropTypes.func,
    handleEditClick: PropTypes.func,
    handleViewDetails: PropTypes.func,
};

export default GuardianTable;
